import { useMemo } from 'react';
import { Row, View } from '@amzn/stencil-react-components/layout';
import { Slider } from '@amzn/stencil-react-components/slider';

import { OptionSelect, type OptionSelectItem } from './OptionSelect';

export interface AuditScoreFieldProps {
  /** Current score. */
  readonly value: number;
  /** Emits the new score from the slider or the numeric select. Omitted
   *  internally (both controls) when disabled. */
  readonly onChange: (value: number) => void;
  /** Lowest selectable score (inclusive). */
  readonly min: number;
  /** Highest selectable score (inclusive). */
  readonly max: number;
  /**
   * id of the visible label element for this field. Wired to the slider via
   * `aria-labelledby` so assistive tech announces the label with the value.
   */
  readonly labelId: string;
  /**
   * Builds the `aria-valuetext` announced on each step, e.g.
   * `(v) => \`\${v} out of \${max}\``. Keeps the spoken value human-readable
   * rather than a bare number.
   */
  readonly formatValueText: (value: number) => string;
  /** Accessible name for the numeric select (which carries no visible label). */
  readonly selectAriaLabel: string;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

const SLIDER_STEP = 1;
const CONTROL_GAP = 'dimensions.spacing.300';
/** Slider : select width ratio (7/8 : 1/8) — the slider spans the row with a
 *  compact numeric select on the right, matching the manual question flow. */
const SLIDER_FLEX = 7;
const SELECT_FLEX = 1;

/**
 * Audit-score picker: a Stencil `Slider` paired with a numeric `OptionSelect`
 * (1–max), matching the manual question flow where the score can be dragged OR
 * chosen from a dropdown. Both controls are bound to the same `value` and emit
 * through the same `onChange`, so they stay in sync.
 *
 * Stencil's Slider has no label, no `aria-label`, and no `disabled` prop, so
 * this wrapper owns accessibility (label wired through `aria-labelledby`,
 * per-step announcement through `aria-valuetext`) and gates the disabled state
 * by withholding `onChange` — a slider with no change handler can't be moved.
 * Controlled: the owner holds the score and updates it on change.
 */
export function AuditScoreField({
  value,
  onChange,
  min,
  max,
  labelId,
  formatValueText,
  selectAriaLabel,
  disabled = false,
  dataTestId,
}: AuditScoreFieldProps): JSX.Element {
  // Numeric options min..max, memoised so the array identity is stable across
  // renders (OptionSelect compares options by reference).
  const scoreOptions = useMemo<readonly OptionSelectItem[]>(() => {
    const options: OptionSelectItem[] = [];
    for (let score = min; score <= max; score += SLIDER_STEP) {
      options.push({ value: String(score), label: String(score) });
    }
    return options;
  }, [min, max]);

  const handleSelectChange = (next: string): void => {
    const parsed = Number(next);
    if (!Number.isNaN(parsed)) onChange(parsed);
  };

  return (
    <Row alignItems="center" gridGap={CONTROL_GAP} width="100%">
      {/* Slider takes 2/3 of the row, the numeric select 1/3. */}
      <View flex={SLIDER_FLEX} minWidth={0}>
        <Slider
          min={min}
          max={max}
          value={value}
          step={SLIDER_STEP}
          aria-labelledby={labelId}
          aria-valuetext={formatValueText(value)}
          {...(disabled ? {} : { onChange })}
          {...(dataTestId ? { dataTestId } : {})}
        />
      </View>
      <View flex={SELECT_FLEX} minWidth={0}>
        <OptionSelect
          options={scoreOptions}
          value={String(value)}
          onChange={handleSelectChange}
          disabled={disabled}
          ariaLabel={selectAriaLabel}
          {...(dataTestId ? { dataTestId: `${dataTestId}-select` } : {})}
        />
      </View>
    </Row>
  );
}
